import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Factory, Search, ShieldAlert } from "lucide-react";

export default function BulkManufacturerAssign() {
  const [products, setProducts] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [manuFilter, setManuFilter] = useState("no_manufacturer"); // no_manufacturer | with_manufacturer | all
  const [manuId, setManuId] = useState("");
  const [division, setDivision] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    const params = { filter: manuFilter === "all" ? "active" : manuFilter, limit: 500 };
    if (q) params.q = q;
    if (categoryId) params.category_id = categoryId;
    const r = await api.get("/products", { params });
    setProducts(r.data);
  };
  useEffect(() => { load(); }, [manuFilter, categoryId]);
  useEffect(() => { api.get("/manufacturers").then((r) => setManufacturers(r.data)).catch(() => {}); api.get("/product-categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);

  const manufacturer = useMemo(() => manufacturers.find((m) => m.id === manuId), [manuId, manufacturers]);
  const divisions = manufacturer?.divisions || [];

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === products.length) setSelected(new Set());
    else setSelected(new Set(products.map((p) => p.id)));
  };

  const hasExisting = useMemo(() => products.some((p) => selected.has(p.id) && p.manufacturer_id), [products, selected]);

  const apply = async () => {
    if (selected.size === 0) return toast.error("Seleziona almeno un prodotto");
    if (!manuId) return toast.error("Seleziona un produttore");
    if (hasExisting && !overwrite) return toast.error("Alcuni prodotti hanno già un produttore. Attiva 'sovrascrivi' per confermare.");
    if (!window.confirm(`Assegnare '${manufacturer?.name}' a ${selected.size} prodotti? ${hasExisting ? "Sovrascriverai i produttori esistenti." : ""}`)) return;
    setBusy(true);
    try {
      const r = await api.post("/catalog/products/bulk-assign-manufacturer", {
        product_ids: Array.from(selected),
        manufacturer_id: manuId,
        manufacturer_name: manufacturer?.name || "",
        division,
        overwrite_existing: overwrite,
      });
      setResult(r.data);
      toast.success(`Aggiornati: ${r.data.updated}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-4" data-testid="bulk-manufacturer-page">
      <Link to="/catalogo" className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-[#d63384]"><ArrowLeft className="w-4 h-4" /> Catalogo prodotti</Link>
      <div>
        <div className="text-xs uppercase tracking-widest text-[#d63384] font-semibold">Catalogo</div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-[#0a0a2e]">Assegnazione multipla produttore</h1>
        <p className="text-sm text-slate-500">Filtra i prodotti, seleziona quelli da assegnare e imposta un produttore comune. Operazione separata dall'import Geecom.</p>
      </div>

      <Card className="ama-card border-0 shadow-none">
        <CardHeader><CardTitle className="font-heading text-lg">Filtri</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div>
            <Label>Ricerca</Label>
            <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="bulk-search" className="pl-8" value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>e.key==="Enter" && load()} placeholder="Codice, titolo…" />
            </div>
          </div>
          <div><Label>Categoria</Label>
            <select data-testid="bulk-category" value={categoryId} onChange={(e)=>setCategoryId(e.target.value)} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
              <option value="">Tutte</option>
              {categories.map((c)=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div><Label>Stato produttore</Label>
            <select data-testid="bulk-manu-filter" value={manuFilter} onChange={(e)=>setManuFilter(e.target.value)} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
              <option value="no_manufacturer">Solo senza produttore</option>
              <option value="with_manufacturer">Solo con produttore</option>
              <option value="all">Tutti i prodotti attivi</option>
            </select>
          </div>
          <div className="flex items-end"><Button data-testid="bulk-refresh" onClick={load} variant="outline" className="w-full">Aggiorna elenco</Button></div>
        </CardContent>
      </Card>

      <Card className="ama-card border-0 shadow-none">
        <CardHeader><CardTitle className="font-heading text-lg">Prodotti ({products.length}) — selezionati: {selected.size}</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto max-h-[420px]">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50 sticky top-0">
                <tr>
                  <th className="p-2"><input type="checkbox" checked={selected.size===products.length&&products.length>0} onChange={toggleAll} /></th>
                  <th className="text-left p-2">Codice</th>
                  <th className="text-left p-2">Titolo</th>
                  <th className="text-left p-2">Categoria</th>
                  <th className="text-left p-2">Produttore attuale</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p)=>(
                  <tr key={p.id} className="border-t border-slate-100" data-testid={`bulk-row-${p.product_code}`}>
                    <td className="p-2"><input type="checkbox" checked={selected.has(p.id)} onChange={()=>toggle(p.id)} data-testid={`bulk-check-${p.product_code}`} /></td>
                    <td className="p-2 font-mono text-xs">{p.product_code}</td>
                    <td className="p-2">{p.product_title || p.model}</td>
                    <td className="p-2 text-xs">{p.category_name || p.source_category_name || "—"}</td>
                    <td className="p-2 text-xs">{p.manufacturer_name || <span className="text-slate-400 italic">Da assegnare</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {products.length === 0 && <div className="text-center text-slate-500 text-sm py-6">Nessun prodotto per questi filtri.</div>}
          </div>
        </CardContent>
      </Card>

      <Card className="ama-card border-0 shadow-none">
        <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Factory className="w-5 h-5" /> Assegnazione</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>Produttore</Label>
            <select data-testid="bulk-manu" value={manuId} onChange={(e)=>{setManuId(e.target.value); setDivision("");}} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
              <option value="">— Seleziona —</option>
              {manufacturers.map((m)=><option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div>
            <Label>Divisione (opzionale)</Label>
            <select data-testid="bulk-division" value={division} onChange={(e)=>setDivision(e.target.value)} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" disabled={divisions.length===0}>
              <option value="">Nessuna</option>
              {divisions.map((d)=><option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={overwrite} onChange={(e)=>setOverwrite(e.target.checked)} data-testid="bulk-overwrite" /> Sovrascrivi produttori esistenti</label>
          </div>
        </CardContent>
        <CardContent className="pt-0">
          {hasExisting && (
            <Alert variant="destructive" className="mb-3"><ShieldAlert className="w-4 h-4" /><AlertTitle>Attenzione</AlertTitle><AlertDescription>Alcuni prodotti selezionati hanno già un produttore assegnato. Per procedere attiva &laquo;Sovrascrivi produttori esistenti&raquo; e conferma esplicitamente.</AlertDescription></Alert>
          )}
          <div className="flex justify-end gap-2">
            <Button data-testid="bulk-apply" onClick={apply} disabled={busy || selected.size === 0 || !manuId} className="ama-btn-primary">Applica assegnazione</Button>
          </div>
          {result && (
            <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 p-3 text-sm" data-testid="bulk-result">
              <div><b>Aggiornati:</b> {result.updated}</div>
              <div><b>Saltati (già con produttore):</b> {result.skipped_existing}</div>
              <div><b>Non trovati:</b> {result.not_found}</div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
